import { t } from '@lingui/core/macro'
import {
  loadPdfDocument,
  type PDFDocumentProxy,
} from 'features/knowledgeDocumentViewer/utils/loadPdfDocument'

export type PdfOutlineUnit = {
  title: string
  startPage: number
  endPage: number
}

async function resolveOutlineStartPage(
  pdfDocument: PDFDocumentProxy,
  dest: unknown
): Promise<number | undefined> {
  let destination = dest
  if (typeof destination === 'string') {
    destination = await pdfDocument.getDestination(destination)
  }
  if (!Array.isArray(destination) || destination.length === 0) {
    return undefined
  }

  const pageIndex = await pdfDocument.getPageIndex(destination[0])
  return pageIndex + 1
}

/** Top-level PDF outline entries as page ranges; [] if missing or unresolvable. */
export async function getPdfOutlineUnits(
  pdfUrl: string
): Promise<PdfOutlineUnit[]> {
  let pdfDocument: PDFDocumentProxy | undefined
  try {
    pdfDocument = await loadPdfDocument(pdfUrl)
    const outline = await pdfDocument.getOutline()
    if (!outline || outline.length === 0) {
      return []
    }

    const totalPages = pdfDocument.numPages
    const resolved: Array<{ title: string; startPage: number }> = []

    for (const entry of outline) {
      try {
        const startPage = await resolveOutlineStartPage(pdfDocument, entry.dest)
        if (startPage === undefined) continue
        const title = entry.title?.trim() || t`Page ${startPage}`
        resolved.push({ title, startPage })
      } catch {
        continue
      }
    }

    if (resolved.length === 0) {
      return []
    }

    resolved.sort((a, b) => a.startPage - b.startPage)

    return resolved.map((unit, index) => {
      const nextStart = resolved[index + 1]?.startPage
      const endPage =
        nextStart !== undefined
          ? Math.max(unit.startPage, nextStart - 1)
          : totalPages
      return {
        title: unit.title,
        startPage: unit.startPage,
        endPage,
      }
    })
  } catch {
    return []
  } finally {
    if (pdfDocument) {
      try {
        await pdfDocument.destroy()
      } catch {
        // Ignore destroy errors; outline result is already resolved.
      }
    }
  }
}
